import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { usePortfolio } from '../context/PortfolioContext';
import { useModals } from '../context/ModalContext';
import SettingsDropdown from './SettingsDropdown';
import TradeModal from './modals/TradeModal';
import DividendModal from './modals/DividendModal';
import DepositModal from './modals/DepositModal';
import CompoundInterestModal from './modals/CompoundInterestModal';

function BrandMark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <rect x="3" y="15" width="6" height="11" rx="2" fill="#00d4ff" opacity="0.75" />
      <rect x="13" y="9" width="6" height="17" rx="2" fill="#00d4ff" />
      <rect x="23" y="5" width="6" height="21" rx="2" fill="#0099cc" />
      <path d="M5 12 14 7l5 4 8-8" fill="none" stroke="#7ae7ee" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Layout({ children }) {
  const location = useLocation();
  const { summary, refreshAll, isLoading } = usePortfolio();
  const {
    activeModal,
    payload,
    openTrade,
    openDividend,
    openDeposit,
    openCalculator,
    closeAll,
  } = useModals();

  const [compactOpen, setCompactOpen] = useState(false);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => {
      refreshAll();
    }, 60000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  // Initial load
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const navItems = [
    { path: '/dashboard', label: 'Overview' },
    { path: '/holdings', label: 'Holdings' },
    { path: '/dividends', label: 'Dividends' },
    { path: '/trades', label: 'Trades' },
    { path: '/deposits', label: 'Deposits' },
    { path: '/analysis', label: 'Analysis' },
    { path: '/risk', label: 'Risk' },
  ];

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return `${num >= 0 ? '+' : ''}${(num * 100).toFixed(2)}%`;
  };

  const totalValue = summary?.total_value_gbp || summary?.total_market_value_gbp || 0;
  const totalPnl = summary?.total_pnl_gbp || 0;
  const returnPct = summary?.return_pct || 0;

  const handleRefresh = async () => {
    try {
      await refreshAll();
      toast.success('Data refreshed');
    } catch (e) {
      toast.error('Refresh failed');
    }
  };

  return (
    <div className="layout">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-brand">
            <span className="header-brand-mark"><BrandMark /></span>
            <div className="header-brand-copy">
              <h1><span className="header-brand-portfolio">North</span><span className="header-brand-dashboard">folio</span></h1>
              <p>Portfolio Analytics & Risk Management</p>
            </div>
          </div>
          <div className="header-kpi">
            <div className="header-kpi-label">Total Portfolio</div>
            <div className="header-kpi-value">{formatCurrency(totalValue)}</div>
            <div className={`header-kpi-sub ${returnPct >= 0 ? 'pos' : 'neg'}`}>
              {formatPercent(returnPct)}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-content">
          <div className="nav-tabs">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-tab ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="nav-actions">
            {/* Refresh */}
            <button
              type="button"
              className={`btn btn-secondary btn-sm btn-icon ${isLoading ? 'btn-spin' : ''}`}
              onClick={handleRefresh}
              disabled={isLoading}
              title="Refresh data"
              aria-label="Refresh data"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>

            {/* Calculator */}
            <button
              type="button"
              className="btn btn-secondary btn-sm btn-icon"
              onClick={openCalculator}
              title="Compound interest calculator"
              aria-label="Open calculator"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <line x1="8" y1="6" x2="16" y2="6" />
                <line x1="8" y1="10" x2="8" y2="10" />
                <line x1="12" y1="10" x2="12" y2="10" />
                <line x1="16" y1="10" x2="16" y2="10" />
                <line x1="8" y1="14" x2="8" y2="14" />
                <line x1="12" y1="14" x2="12" y2="14" />
                <line x1="16" y1="14" x2="16" y2="14" />
                <line x1="8" y1="18" x2="12" y2="18" />
                <line x1="16" y1="18" x2="16" y2="18" />
              </svg>
            </button>

            {/* Full action buttons (desktop) */}
            <div className="nav-actions-full">
              <button
                type="button"
                className="btn btn-sm btn-action btn-action-trade"
                onClick={() => openTrade()}
              >
                + Trade
              </button>
              <button
                type="button"
                className="btn btn-sm btn-action btn-action-dividend"
                onClick={() => openDividend()}
              >
                + Dividend
              </button>
              <button
                type="button"
                className="btn btn-sm btn-action btn-action-deposit"
                onClick={() => openDeposit()}
              >
                + Deposit
              </button>
            </div>

            {/* Compact "+ New" dropdown (mobile) */}
            <div className="nav-actions-compact" style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-sm btn-action btn-action-trade"
                onClick={() => setCompactOpen((v) => !v)}
                aria-expanded={compactOpen}
              >
                + New ▾
              </button>
              {compactOpen && (
                <div
                  className="settings-menu"
                  role="menu"
                  onClick={() => setCompactOpen(false)}
                >
                  <button className="settings-menu-item" onClick={() => openTrade()}>
                    + Trade
                  </button>
                  <button className="settings-menu-item" onClick={() => openDividend()}>
                    + Dividend
                  </button>
                  <button className="settings-menu-item" onClick={() => openDeposit()}>
                    + Deposit
                  </button>
                </div>
              )}
            </div>

            {/* Settings (username + sign out) */}
            <SettingsDropdown />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">{children}</main>

      {/* Global modals (single instance each) */}
      <TradeModal
        isOpen={activeModal === 'trade'}
        onClose={closeAll}
        trade={activeModal === 'trade' ? payload : null}
      />
      <DividendModal
        isOpen={activeModal === 'dividend'}
        onClose={closeAll}
        dividend={activeModal === 'dividend' ? payload : null}
      />
      <DepositModal
        isOpen={activeModal === 'deposit'}
        onClose={closeAll}
        deposit={activeModal === 'deposit' ? payload : null}
      />
      <CompoundInterestModal
        isOpen={activeModal === 'calculator'}
        onClose={closeAll}
      />
    </div>
  );
}

export default Layout;
